import { Icon } from "@afa-shared/afa-components";
import DateSign from "@afa-shared/afa-components/dist/DateSign";
import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Message from "@afa-shared/afa-components/dist/Message";
import Tag from "@afa-shared/afa-components/dist/Tag";
import Text from "@afa-shared/afa-components/dist/Text";
import { PrintButton } from "@components/PrintButton";
import RichTextRenderer from "@components/RichTextRenderer";
import {
  RegistrationButtonSection,
  RegistrationTextSection,
} from "@containers/EventPage/RegistrationSection";
import ArticlePageWrapper from "@containers/Layout/PageWrapper/ArticlePageWrapper";
import { defaultContext } from "@utils/contextHelper/getInitialContext";
import { shouldRemoveBottomMargin } from "@utils/layoutHelpers/layoutMarginHelper";
import { getContentAreaMaxWidth } from "@utils/pageHelper";
import { shouldUseReCapcha } from "@utils/reCapchaHelper";

import { Context } from "types/context/Context";
import { HeadingContext } from "types/context/HeadingContext";

import CFContentArea from "../../components/cFContentArea";
import pageStyles from "../../styles/page.module.css";
import eventPageStyles from "./EventPage.module.css";
import { EventPageType_eventpage } from "./queries/contentfulTypes/EventPageType";

export interface ExtendedEventData extends EventPageType_eventpage {
  dateSignDay: string;
  dateSignMonth: string;
  isPastEvent: boolean;
  physicalRegistrationIsOpen: boolean;
  remoteRegistrationIsOpen: boolean;
  lastRegistrationDatePhysicalString: string;
  lastRegistrationDateRemoteString: string;
  eventDatesStringArray: string[];
  eventShortDate: string;
  hasNoRegistrationDatesSet: boolean;
}

const EventPage = (props: any) => {
  const eventPage: ExtendedEventData = props?.pageData;
  let context: Context = { ...defaultContext };
  const imageData = eventPage?.ivMainImage?.[0];
  const useReCapcha = shouldUseReCapcha(
    [].concat(eventPage?.mainAreaCollection?.items, eventPage?.mainAreaTopCollection?.items)
  );
  const removeLayoutBottomMargin = shouldRemoveBottomMargin(eventPage?.mainAreaCollection?.items);
  const showMessage = !(
    eventPage?.hasNoRegistrationDatesSet ||
    eventPage?.physicalRegistrationIsOpen ||
    eventPage?.remoteRegistrationIsOpen
  );

  const headingContext: HeadingContext = {
    parentLevel: eventPage?.headingCompletedEvents ? "h3" : "h2",
    childLevel: eventPage?.headingCompletedEvents ? "h4" : "h3",
  };
  context = { ...context, heading: headingContext };

  return (
    <ArticlePageWrapper
      imageData={imageData}
      topImageText={eventPage?.topImageText}
      columns={8}
      relatedContentHeading={eventPage?.relatedContentHeading}
      relatedContentCollection={eventPage?.relatedContentCollection}
      relatedContentSmallImages={eventPage?.relatedContentSmallImages}
      context={context}
      useReCapcha={useReCapcha}
      shouldRemoveBottomMargin={removeLayoutBottomMargin}
      className={eventPageStyles.eventPage}
      contentfulPreviewEntryId={eventPage.sys.id}
      contentfulPreviewImageFieldId={"ivMainImage"}
      contentfulPreviewImageTextFieldId="topImageText"
      contentfulPreviewRelatedContentHeadingFieldId={"relatedContentHeading"}
    >
      <Grid container desktop={8}>
        <div style={{ position: "relative" }}>
          <div className={eventPageStyles.dateSignWrapper}>
            <DateSign day={eventPage?.dateSignDay} month={eventPage?.dateSignMonth} />
          </div>
        </div>
        <Heading
          className={eventPageStyles.heading}
          variant="h1"
          data-contentful-entry-id={eventPage.sys.id}
          data-contentful-field-id="heading"
        >
          {eventPage?.heading}
        </Heading>
        <div className={eventPageStyles.metaWrapper}>
          <div className={eventPageStyles.tagWrapper}>
            <Tag
              label={eventPage?.eventCategory}
              data-contentful-entry-id={eventPage.sys.id}
              data-contentful-field-id="eventCategory"
            />
            {eventPage?.eventFormat && (
              <Tag
                label={eventPage?.eventFormat}
                data-contentful-entry-id={eventPage.sys.id}
                data-contentful-field-id="eventFormat"
              />
            )}
          </div>
        </div>
        {showMessage && (
          <Message
            type="warning"
            heading={" Det går inte längre att anmäla sig till det här evenemanget."}
            headingVariant="paragraph"
            className={eventPageStyles.warningMessage}
          />
        )}

        {eventPage?.preamble && (
          <Text
            variant={"preamble"}
            children={eventPage?.preamble}
            data-contentful-entry-id={eventPage.sys.id}
            data-contentful-field-id="preamble"
          />
        )}

        {!eventPage.isPastEvent && (
          <div className={eventPageStyles.infoWrapper}>
            <div
              className={eventPageStyles.infoField}
              data-contentful-entry-id={eventPage.sys.id}
              data-contentful-field-id="endDate"
            >
              <Icon name="date_range" />
              <div>
                <Text variant="span" weight="bold">
                  Datum och tid
                </Text>
                <div className="dateWrapper">
                  {eventPage?.eventDatesStringArray?.map((item, index) => (
                    <Text variant="span" key={index}>
                      {item}
                    </Text>
                  ))}
                </div>
                <Text variant="span">{eventPage.additionalTimeText}</Text>
              </div>
            </div>
            {eventPage?.location?.json && (
              <div
                className={eventPageStyles.infoField}
                data-contentful-entry-id={eventPage.sys.id}
                data-contentful-field-id="location"
              >
                <Icon name="location" />
                <div>
                  <Text variant="span" weight="bold">
                    Plats
                  </Text>

                  <RichTextRenderer
                    json={eventPage?.location?.json}
                    links={eventPage?.location?.links}
                    pageTextArea
                  />
                </div>
              </div>
            )}
            {eventPage?.cost && (
              <div
                className={eventPageStyles.infoField}
                data-contentful-entry-id={eventPage.sys.id}
                data-contentful-field-id="cost"
              >
                <Icon name="cost" />
                <div>
                  <Text variant="span" weight="bold">
                    Kostnad
                  </Text>

                  <Text variant="span">{eventPage.cost}</Text>
                </div>
              </div>
            )}
            {eventPage.eventShortInfo && (
              <div
                className={eventPageStyles.infoField}
                data-contentful-entry-id={eventPage.sys.id}
                data-contentful-field-id="eventShortInfo"
              >
                <Icon name="info" />
                <div>
                  <Text variant="span" weight="bold">
                    Övrig information
                  </Text>
                  <Text variant="span">{eventPage.eventShortInfo}</Text>
                </div>
              </div>
            )}
            {!eventPage?.hasNoRegistrationDatesSet && (
              <div className={eventPageStyles.registrationField}>
                <div className={eventPageStyles.infoField}>
                  <Icon name="how_to_reg" />
                  <div>
                    <Text variant="span" weight="bold">
                      Anmälan
                    </Text>
                    {RegistrationTextSection(eventPage)}
                  </div>
                </div>

                {RegistrationButtonSection(eventPage)}
              </div>
            )}
          </div>
        )}

        <div className={pageStyles.contentAreaGrid}>
          {(eventPage?.headingCompletedEvents ||
            eventPage?.mainAreaTopCollection?.items?.length > 0) && (
            <div className={eventPageStyles.completedEventsSection}>
              {eventPage?.headingCompletedEvents && (
                <Heading
                  className={eventPageStyles.headingCompletedEvents}
                  variant={"h2"}
                  data-contentful-entry-id={eventPage.sys.id}
                  data-contentful-field-id="headingCompletedEvents"
                >
                  {eventPage?.headingCompletedEvents}
                </Heading>
              )}
              {/* Main area for recorded events (videoblock) or presentations (fileblock)*/}
              {eventPage?.mainAreaTopCollection?.items?.length > 0 && (
                <CFContentArea
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  key="main-area"
                  contentAreaItems={eventPage?.mainAreaTopCollection?.items}
                  maxWidth={getContentAreaMaxWidth(8)}
                  context={context}
                  heading={context.heading?.parentLevel}
                />
              )}
            </div>
          )}
          {eventPage?.mainBody?.json && (
            <div data-contentful-entry-id={eventPage.sys.id} data-contentful-field-id="mainBody">
              <RichTextRenderer
                json={eventPage?.mainBody?.json}
                links={eventPage?.mainBody?.links}
                pageTextArea
              />
            </div>
          )}

          {eventPage?.mainAreaCollection?.items?.length > 0 && (
            <>
              <CFContentArea
                desktopColumns={1}
                mobileColumns={1}
                tabletColumns={1}
                key="main-area"
                contentAreaItems={eventPage?.mainAreaCollection?.items}
                maxWidth={getContentAreaMaxWidth(8)}
                context={context}
              />
            </>
          )}
        </div>
        <PrintButton />
      </Grid>
    </ArticlePageWrapper>
  );
};

export default EventPage;
